import Cleave from "cleave.js/react";
import {
  MDBAlert,
  MDBBtn,
  MDBCol,
  MDBContainer,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBRow,
} from "mdbreact";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { CampaignContainer } from "../../../containers/Campaign";
import { UserContainer } from "../../../containers/User";
import formatDate from "../../../utilities/DateFormatter";
import CampaignImage from "../../elements/campaign-image/CampaignImage";
import TextDecoration from "../../modules/text-decoration/TextDecoration";
import classes from "./LoginPage.module.scss";

/**
 * エントリー画面コンポーネント
 */
const LoginPage: React.FC = () => {
  const history = useHistory();
  const userContainer = UserContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();

  const user = userContainer.user;

  const campaign = campaignContainer.campaign;

  const dateFormat = "YYYY年MM月DD日(AA)";
  const campaignStartDate = formatDate(dateFormat, campaign?.from);

  /** フォームに入力された文字列 */
  const [inputBlockValue, setInputBlockValue] = useState<string>("");
  /** エラー文（入力された値が不正な場合） */
  const [hasInvalidError, setHasInvalidError] = useState<boolean>(false);
  /** 確認モーダルの展開状態 */
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  /** モーダル表示用のポイント番号 */
  const [displayPointNum, setDisplayPointNum] = useState<string>("");

  useEffect(() => {
    if (user.id) {
      // ユーザー情報を保持している場合、エントリー画面に遷移
      history.push("/entry");
    }
  }, [history, user.id]);

  /**
   * ポイントナンバーの入力イベント
   */
  const handleChangePointNumber = (rawValue: string, displayValue: string) => {
    setInputBlockValue(rawValue);
    setDisplayPointNum(displayValue);
  };

  /**
   * 登録ボタン押下時の処理
   */
  const handleRegistrationClick = () => {
    setHasInvalidError(false);

    // 入力内容のバリデーション (12桁または16桁の数字)
    const reg = new RegExp(/^([0-9]{12})$|^([0-9]{16})$/);
    if (reg.test(inputBlockValue)) {
      setIsModalOpen(true);
    } else {
      // エラー文を表示
      setHasInvalidError(true);
    }
  };

  /**
   * モーダル上の登録確定ボタンを押下したときの処理
   */
  const handleCommitClick = () => {
    // local Storage への保存
    userContainer.login(inputBlockValue).then(() => {
      // 初期スタンプページに遷移
      history.push("/entry");
    });
  };

  return (
    <MDBContainer>
      <CampaignImage className="mt-3" image={campaign?.image || ""} />

      {campaign ? (
        <React.Fragment>
          <div className={`${classes.campaignDescription} py-4 text-center`}>
            <TextDecoration sentences={campaign?.description} />

            <div className="mt-3 text-center">
              <div className={`py-2 px-3 d-inline-block ${classes.caution}`}>
                <MDBIcon
                  icon="exclamation-circle"
                  className="mr-1 mr-sm-2"
                ></MDBIcon>
                ご参加には、三井ショッピングパークポイントの<br></br>
                ポイントナンバーが必要です
              </div>
            </div>

            <div className={`mt-3 ${classes.externalLink}`}>
              スタンプラリーの詳細は
              <a
                href={campaign?.detail}
                className="mx-1"
                target="_blank"
                rel="noopener noreferrer"
              >
                こちら
              </a>
            </div>
          </div>

          <hr className={classes.hLine}></hr>

          {campaign.status !== "preparing" ? (
            <React.Fragment>
              <div className={`text-center mt-4 ${classes.stepMessage}`}>
                <div className={`${classes.title}`}>STEP1</div>
                <div className={classes.text}>
                  ポイントナンバーを登録しましょう
                </div>
              </div>

              <div className="py-4 px-3 d-flex justify-content-center">
                <div className={classes.pointNumberInput}>
                  <div className={`mb-2 ${classes.hintText}`}>
                    ポイントナンバーを入力（12桁 または 16桁の数字）
                  </div>
                  <div className="form-group d-flex align-items-center mb-0">
                    <Cleave
                      className={`${classes.inputField} form-control text-center`}
                      type="tel"
                      options={{
                        blocks: [4, 4, 4, 4],
                        delimiter: "-",
                        delimiterLazyShow: true,
                        numericOnly: true,
                      }}
                      value={inputBlockValue}
                      onChange={(event) =>
                        handleChangePointNumber(
                          event.target.rawValue,
                          event.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              <MDBRow
                className={`${classes.actionButtonContainer} mb-2 justify-content-center`}
              >
                {hasInvalidError ? (
                  <MDBCol size="12" className="d-flex justify-content-center">
                    <MDBAlert color="danger" className={classes.alert}>
                      入力した値が不正です。12桁 または 16桁
                      の数字を入力してください。
                    </MDBAlert>
                  </MDBCol>
                ) : null}

                <MDBCol size="12" className="d-flex justify-content-center">
                  <div className={`${classes.annotation}`}>
                    <div
                      className={`py-2 px-3 d-inline-block ${classes.message}`}
                    >
                      <TextDecoration sentences={campaign?.privacyPolicy} />
                    </div>
                  </div>
                </MDBCol>

                <MDBCol
                  size="12"
                  className="my-3 d-flex justify-content-center"
                >
                  <MDBBtn
                    className={`${classes.entryButton} my-0 py-1`}
                    color="yellow"
                    onClick={handleRegistrationClick}
                  >
                    ご同意の上<br></br>
                    ポイントナンバーを登録する
                  </MDBBtn>
                </MDBCol>

                <MDBCol size="12" className="d-flex justify-content-center">
                  <div className={`${classes.externalLink}`}>
                    ポイントナンバーの確認方法は
                    <a
                      href={campaign?.pointNumberGuide}
                      className="mx-1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      こちら
                    </a>
                  </div>
                </MDBCol>
                <MDBCol size="12" className="d-flex justify-content-center">
                  <div className={`mt-2 text-center ${classes.externalLink}`}>
                    三井ショッピングパークポイント会員
                    <br className="d-md-none"></br>
                    新規入会は
                    <a
                      href={campaign?.newMemberCheck}
                      className="mx-1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      こちら
                    </a>
                  </div>
                </MDBCol>
              </MDBRow>
            </React.Fragment>
          ) : (
            <div className={`text-center mx-3 mt-4 py-3`}>
              <div className={`alert alert-danger ${classes.alert}`}>
                <React.Fragment>
                  本キャンペーンは<br></br>
                  <span className="mx-1">{campaignStartDate}</span>
                  より開始します。<br></br>
                  キャンペーンの開始までお待ちください。
                </React.Fragment>
              </div>
            </div>
          )}
        </React.Fragment>
      ) : null}

      <MDBModal isOpen={isModalOpen} toggle={() => {}} centered>
        <MDBModalBody className={`py-5 ${classes.checkModal}`}>
          <div className="text-center">
            こちらのポイントナンバーで
            <br />
            スタンプラリーをはじめます
          </div>
          <div className={`text-center py-3 ${classes.pointNumberText}`}>
            {displayPointNum}
          </div>
          <div className={`text-center ${classes.cautionMessage}`}>
            入力されたポイントナンバーが間違っている場合
            <br />
            スタンプが正しく付与されませんのでご注意ください
          </div>
          <MDBRow
            className={`${classes.buttonContainer} mt-3 pt-3 justify-content-center`}
          >
            <MDBCol size="12" className="mb-3 d-flex justify-content-center">
              <MDBBtn
                className={`${classes.startButton} my-0 py-3`}
                color="yellow"
                onClick={handleCommitClick}
              >
                ポイントナンバーを登録する
              </MDBBtn>
            </MDBCol>
            <MDBCol size="12" className="d-flex justify-content-center">
              <MDBBtn
                className={`${classes.cancelButton} my-0 py-2`}
                color="yellow"
                onClick={() => setIsModalOpen(false)}
              >
                戻る
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBModalBody>
      </MDBModal>
    </MDBContainer>
  );
};

export default LoginPage;
